import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { canSee, homeFor, ROLE_LABELS } from "../lib/areas";
import { SUBNAV } from "../lib/subnav";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, Users, ClipboardList, HardHat, Tag, Package,
  CalendarDays, Wallet, BarChart3, LogOut, Menu, X, Settings as SettingsIcon, FileText,
  Globe2, Inbox, Building2, ShieldCheck, Star, Megaphone, Trophy, UserCog, Plug, Paintbrush, ImageIcon, ShieldAlert, KeyRound, Stethoscope, Archive, Rocket, MessagesSquare,
  Ticket, ShoppingBag, ToggleRight, ChevronRight,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

// badge: bildirim sayacı anahtarı (/notifications/summary)
const navGroups = [
  {
    title: "Operasyon",
    items: [
      { to: "/admin", icon: LayoutDashboard, label: "Genel Bakış", end: true, testid: "nav-dashboard" },
      { to: "/admin/jobs", icon: ClipboardList, label: "Servis Kayıtları", testid: "nav-jobs" },
      { to: "/admin/quotes", icon: FileText, label: "Fiyat Teklifleri", testid: "nav-quotes" },
      { to: "/admin/appointments", icon: CalendarDays, label: "Randevular", testid: "nav-appointments" },
      { to: "/admin/customers", icon: Users, label: "Müşteriler", testid: "nav-customers" },
      { to: "/admin/growth", icon: Rocket, label: "Büyüme Merkezi", testid: "nav-growth" },
      { to: "/admin/coupons", icon: Ticket, label: "Kuponlar", testid: "nav-coupons" },
      { to: "/admin/warranty", icon: ShieldCheck, label: "Garanti Talepleri", testid: "nav-warranty", badge: "open_warranty", tone: "amber" },
    ],
  },
  {
    title: "Web & Pazarlama",
    items: [
      { to: "/admin/marketing", icon: Megaphone, label: "Pazarlama Merkezi", testid: "nav-marketing" },
      { to: "/admin/visitors", icon: Globe2, label: "Ziyaretçiler", testid: "nav-visitors", badge: "live_visitors", tone: "emerald", live: true },
      { to: "/admin/messages", icon: Inbox, label: "İletişim Mesajları", testid: "nav-messages", badge: "new_messages", tone: "red" },
      { to: "/admin/dealers", icon: Building2, label: "Bayilik Başvuruları", testid: "nav-dealers", badge: "new_dealers", tone: "red" },
      { to: "/admin/reviews", icon: Star, label: "Yorumlar", testid: "nav-reviews", badge: "pending_reviews", tone: "amber" },
    ],
  },
  {
    title: "Site Yönetimi",
    items: [
      { to: "/admin/site-studio", icon: Paintbrush, label: "Site Stüdyosu", testid: "nav-site-studio" },
      { to: "/admin/shop", icon: ShoppingBag, label: "Mağaza", testid: "nav-shop" },
      { to: "/admin/site-features", icon: ToggleRight, label: "Site Özellikleri", testid: "nav-site-features" },
      { to: "/admin/media", icon: ImageIcon, label: "Medya Kütüphanesi", testid: "nav-media" },
    ],
  },
  {
    title: "Ekip & Stok",
    items: [
      { to: "/admin/technicians", icon: HardHat, label: "Teknisyenler", testid: "nav-technicians" },
      { to: "/admin/team", icon: MessagesSquare, label: "Ekip Merkezi", testid: "nav-team", badge: "chat_unread", tone: "red" },
      { to: "/admin/performance", icon: Trophy, label: "Performans", testid: "nav-performance" },
      { to: "/admin/announcements", icon: Megaphone, label: "Duyurular", testid: "nav-announcements" },
      { to: "/admin/diagnosis", icon: Stethoscope, label: "Arıza Rehberi", testid: "nav-diagnosis" },
      { to: "/admin/brands", icon: Tag, label: "Markalar", testid: "nav-brands" },
      { to: "/admin/parts", icon: Package, label: "Yedek Parça", testid: "nav-parts" },
    ],
  },
  {
    title: "Finans & Raporlar",
    items: [
      { to: "/admin/accounting", icon: Wallet, label: "Muhasebe", testid: "nav-accounting" },
      { to: "/admin/reports", icon: BarChart3, label: "Raporlar", testid: "nav-reports" },
    ],
  },
  {
    title: "Sistem",
    items: [
      { to: "/admin/security", icon: ShieldAlert, label: "Güvenlik", testid: "nav-security", badge: "security_alerts", tone: "red" },
      { to: "/admin/account", icon: KeyRound, label: "Hesabım", testid: "nav-account" },
      { to: "/admin/governance", icon: Archive, label: "Yedek & Denetim", testid: "nav-governance" },
      { to: "/admin/users", icon: UserCog, label: "Kullanıcılar", testid: "nav-users" },
      { to: "/admin/integration", icon: Plug, label: "Site Entegrasyonu", testid: "nav-integration" },
      { to: "/admin/settings", icon: SettingsIcon, label: "Ayarlar", testid: "nav-settings" },
    ],
  },
];

const BADGE_TONES = { red: "bg-red-500 text-white", amber: "bg-amber-500 text-white", emerald: "bg-emerald-500 text-white" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState({});
  const [expanded, setExpanded] = useState({});
  const prev = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Bildirim sayaçları: 30 sn'de bir yenilenir, yeni mesaj/başvuru gelince bildirim gösterilir
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications/summary");
        if (stop) return;
        const p = prev.current;
        if (p) {
          if (data.new_messages > p.new_messages) toast.info(`Yeni iletişim mesajı${data.latest_message ? `: ${data.latest_message.name}` : ""}`, { action: { label: "Aç", onClick: () => navigate("/admin/messages") } });
          if (data.new_dealers > p.new_dealers) toast.info(`Yeni bayilik başvurusu${data.latest_dealer ? `: ${data.latest_dealer.company}` : ""}`, { action: { label: "Aç", onClick: () => navigate("/admin/dealers") } });
        }
        prev.current = data;
        let chat = 0;
        try { chat = (await api.get("/chat/unread")).data.unread; } catch { /* yetkisiz roller */ }
        setNotif({ ...data, chat_unread: chat });
      } catch { /* sessiz */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [navigate]);

  if (user && !canSee(user, location.pathname)) return <Navigate to={homeFor(user)} replace />;

  const Sidebar = () => (
    <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2.5">
        <img src="/brand/logo.png" alt="ServisPlus" className="h-11 w-auto object-contain" />
        <div>
          <div className="font-display font-bold text-base text-slate-900 leading-none">ServisPlus</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Yönetim Paneli</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {navGroups.map((g) => ({ ...g, items: g.items.filter((i) => canSee(user, i.to)) })).filter((g) => g.items.length).map((g) => (
          <div key={g.title}>
            <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{g.title}</div>
            <div className="space-y-0.5">
              {g.items.map((item) => {
                const count = item.badge ? notif[item.badge] || 0 : 0;
                const kids = SUBNAV[item.to];
                const isOn = location.pathname.startsWith(item.to);
                const isOpen = !!kids && (expanded[item.to] ?? isOn);
                const link = (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-blue-50 text-blue-700 border border-blue-100"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {kids && (
                      <button type="button" aria-label="Alt menüyü aç/kapat" data-testid={`${item.testid}-toggle`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((x) => ({ ...x, [item.to]: !(x[item.to] ?? isOn) })); }}
                        className="p-0.5 -mr-1 rounded hover:bg-slate-200/60">
                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </button>
                    )}
                    {count > 0 && (
                      <span data-testid={`badge-${item.badge}`} className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${BADGE_TONES[item.tone]} ${item.live ? "" : ""}`}>
                        {item.live && <span className="h-1.5 w-1.5 rounded-full bg-white mr-1 animate-pulse" />}{count}
                      </span>
                    )}
                  </NavLink>
                );
                const curTab = new URLSearchParams(location.search).get("tab") || kids?.[0]?.[0];
                return (
                  <div key={item.to}>
                    {link}
                    {isOpen && (
                      <div className="ml-5 mt-0.5 mb-1 pl-3 border-l border-slate-200 space-y-0.5" data-testid={`${item.testid}-sub`}>
                        {kids.map(([k, l]) => (
                          <Link key={k} to={`${item.to}?tab=${k}`} onClick={() => setOpen(false)} data-testid={`${item.testid}-${k}`}
                            className={`block px-3 py-1.5 rounded-md text-[13px] transition-colors ${isOn && curTab === k ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                            {l}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="px-2 py-2 mb-2">
          <div className="text-xs text-slate-500">Giriş yapan</div>
          <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          {user?.admin_role && <div className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold" data-testid="role-badge">{ROLE_LABELS[user.admin_role] || user.admin_role}</div>}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          data-testid="logout-button"
          className="w-full justify-start gap-2 text-slate-700"
        >
          <LogOut className="h-4 w-4" /> Çıkış Yap
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="relative h-full">
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4">
          <button onClick={() => setOpen(true)} className="p-2 -ml-2 text-slate-700" data-testid="open-sidebar">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to="/admin" className="font-display font-bold text-slate-900">ServisPlus</Link>
          <button onClick={logout} className="p-2 -mr-2 text-slate-700"><LogOut className="h-5 w-5" /></button>
        </header>
        <main className="flex-1 overflow-y-auto p-6 lg:p-8" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
